import React, { useEffect, useState } from "react";
import { Card, Col, Row, Spinner, Table, Badge, Form } from "react-bootstrap";
import { useOutletContext } from "react-router-dom";
import Header from "../components/Header";
import { getHost } from "../utils/utils";
import { FiClock, FiAlertTriangle, FiCheckCircle, FiSearch, FiMonitor, FiPackage } from "react-icons/fi";
import "../components/MonitorsTable.css";

const API_STALE = `${getHost()}/api/admin/stale-monitors`;
const getToken = () => localStorage.getItem("jwt");

function formatAge(seconds) {
  if (seconds == null) return "Never";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  const days = Math.floor(seconds / 86400);
  return `${days}d ago`;
}

function staleBadge(seconds) {
  if (seconds == null)
    return <Badge bg="secondary">Never pinged</Badge>;
  if (seconds > 7 * 86400)
    return <Badge bg="danger">7d+</Badge>;
  if (seconds > 3 * 86400)
    return <Badge bg="warning" text="dark">3d+</Badge>;
  if (seconds > 86400)
    return <Badge bg="info">1d+</Badge>;
  return <Badge bg="success">Active</Badge>;
}

const SummaryCard = ({ icon, label, value, color }) => (
  <Col lg={3} md={6} xs={6} className="mb-3">
    <Card className="border border-dark bg-transparent h-100">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          {icon}
          <span className="text-white-65 fs-12">{label}</span>
        </div>
        <h4 className="fw-600 mb-0" style={{ color: color || "#ADADAD" }}>
          {value}
        </h4>
      </Card.Body>
    </Card>
  </Col>
);

const StaleMonitors = () => {
  const { toggleSidebar } = useOutletContext();
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all, stale, never, active

  useEffect(() => {
    fetch(API_STALE, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((res) => res.json())
      .then((data) => setMonitors(data.monitors || []))
      .catch(() => setMonitors([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = monitors.filter((m) => {
    if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === "never") return m.seconds_since_ping == null;
    if (filter === "stale") return m.seconds_since_ping != null && m.seconds_since_ping > 3 * 86400;
    if (filter === "active") return m.seconds_since_ping != null && m.seconds_since_ping <= 86400;
    return true;
  });

  const neverPinged = monitors.filter((m) => m.seconds_since_ping == null).length;
  const stale7d = monitors.filter((m) => m.seconds_since_ping != null && m.seconds_since_ping > 7 * 86400).length;
  const stale3d = monitors.filter((m) => m.seconds_since_ping != null && m.seconds_since_ping > 3 * 86400).length;
  const active = monitors.filter((m) => m.seconds_since_ping != null && m.seconds_since_ping <= 86400).length;

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="pb-4 mx-4 content-body">
        <Row className="gx-0 align-items-center">
          <Col md={12}>
            <div className="mb-2">
              <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
                <FiClock className="text-white" />
              </div>
              <h4 className="fs-22 text-main mb-1">Stale Monitors</h4>
              <p className="text-white-65 fs-14 mb-0">
                Enabled monitors sorted by how long since their last webhook ping.
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-4 pb-4">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="light" />
          </div>
        ) : (
          <>
            <Row>
              <SummaryCard
                icon={<FiAlertTriangle className="text-danger fs-18" />}
                label="Never Pinged"
                value={neverPinged}
                color={neverPinged > 0 ? "#FF4444" : "#00E98E"}
              />
              <SummaryCard
                icon={<FiClock className="text-warning fs-18" />}
                label="Stale (3d+)"
                value={stale3d}
                color={stale3d > 0 ? "#FFC107" : "#00E98E"}
              />
              <SummaryCard
                icon={<FiClock className="text-info fs-18" />}
                label="Stale (7d+)"
                value={stale7d}
                color={stale7d > 0 ? "#FF4444" : "#00E98E"}
              />
              <SummaryCard
                icon={<FiCheckCircle className="text-success fs-18" />}
                label="Active (24h)"
                value={active}
              />
            </Row>

            <Card className="border border-dark bg-transparent">
              <Card.Body className="p-3">
                <div className="d-flex gap-3 mb-3 align-items-center">
                  <div className="position-relative flex-grow-1" style={{ maxWidth: 300 }}>
                    <FiSearch
                      className="position-absolute text-white-65"
                      style={{ left: 12, top: "50%", transform: "translateY(-50%)" }}
                    />
                    <Form.Control
                      type="text"
                      placeholder="Search monitors..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="form-input-field ps-5 fs-13"
                      style={{ background: "#141414", border: "1px solid #333", color: "#fff" }}
                    />
                  </div>
                  <Form.Select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="fs-13"
                    style={{ background: "#141414", border: "1px solid #333", color: "#fff", width: 160 }}
                  >
                    <option value="all">All ({monitors.length})</option>
                    <option value="never">Never Pinged ({neverPinged})</option>
                    <option value="stale">Stale 3d+ ({stale3d})</option>
                    <option value="active">Active 24h ({active})</option>
                  </Form.Select>
                </div>

                <div className="table-mobile-scroll">
                  <Table className="table custom-table table-bordered" hover>
                    <thead className="rounded-3">
                      <tr>
                        <th className="fs-13 fw-500 border-bottom">
                          <div className="d-flex align-items-center gap-2">
                            <FiMonitor className="fs-16" />
                            Monitor
                          </div>
                        </th>
                        <th className="fs-13 fw-500 border-bottom">
                          <div className="d-flex align-items-center gap-2">
                            <FiPackage className="fs-16" />
                            Category
                          </div>
                        </th>
                        <th className="fs-13 fw-500 border-bottom">Products</th>
                        <th className="fs-13 fw-500 border-bottom">Tracked</th>
                        <th className="fs-13 fw-500 border-bottom">
                          <div className="d-flex align-items-center gap-2">
                            <FiClock className="fs-16" />
                            Last Ping
                          </div>
                        </th>
                        <th className="fs-13 fw-500 border-bottom">Status</th>
                        <th className="fs-13 fw-500 border-bottom">Circuit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((m) => (
                        <tr key={m._id}>
                          <td className="fs-13 fw-500 text-main">
                            {m.name}
                            {m.region && (
                              <span className="text-white-65 ms-1 fs-11">{m.region}</span>
                            )}
                          </td>
                          <td className="fs-12 text-white-65">{m.category}</td>
                          <td className="fs-12 text-white-65">{m.product_count}</td>
                          <td className="fs-12 text-white-65">{m.tracked_count}</td>
                          <td className="fs-12 text-white-65">{formatAge(m.seconds_since_ping)}</td>
                          <td className="fs-12">{staleBadge(m.seconds_since_ping)}</td>
                          <td className="fs-12">
                            {m.cb_state === "open" ? (
                              <Badge bg="danger">Open</Badge>
                            ) : m.cb_state === "half_open" ? (
                              <Badge bg="warning" text="dark">Half</Badge>
                            ) : m.cb_state === "unknown" ? (
                              <span className="text-white-65">-</span>
                            ) : (
                              <Badge bg="success">Closed</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center text-white-65 fs-13 py-4">
                            No monitors found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default StaleMonitors;
